import axios from 'axios';
import { route } from 'ziggy-js';
import { router } from '@inertiajs/vue3';

import useBanners from '@/composables/useBanners';
import { useGtm } from '@/composables/useGtm';
import { lettrHomeUrl } from '@/utils/lettrUrls';

/**
 * Dismissible Lettr promo placements. The values are the enum keys the
 * dismissal endpoint accepts (App\Topol\Users\Enums\PromoPlacement), and the
 * suppression window lives on the backend: the template-grid card stays
 * dismissed for good, the strip and the sidebar card come back after 30 days.
 */
export type LettrPromoPlacement =
    | 'lettr_template_card'
    | 'lettr_banner'
    | 'lettr_sidebar';

/**
 * Every place a Lettr promo is measured. The export menu row and the export
 * panel on the editor page are always shown when Lettr is not connected - they
 * are part of the export list rather than banners - so they are tracked but
 * never dismissed.
 */
export type LettrPromoLabel =
    | LettrPromoPlacement
    | 'lettr_export_menu'
    | 'lettr_export_panel'
    // Same placement and same dismissal as `lettr_banner`, tracked apart
    // because the v2 strip is a different audience with different copy.
    | 'lettr_banner_v2';

/**
 * The same square the export list uses for a connected integration, so every
 * promo and the row it becomes are recognisably the same thing.
 */
export const LETTR_LOGO =
    'https://d1vyenbk43iobe.cloudfront.net/application/images/lettr.png';

export const LETTR_TAGLINE = 'Stays editable after export';

/**
 * Read by the available-integrations page, which opens the named integration's
 * modal on arrival. A query param rather than shared state so the link works
 * on a cold load too.
 */
export const CONNECT_QUERY_PARAM = 'connect';

const useLettrPromo = () => {
    const { pushEvent } = useGtm();
    const {
        shouldDisplayLettrPromoTemplateCard,
        shouldDisplayLettrPromoBanner,
        shouldDisplayLettrPromoSidebar,
    } = useBanners();

    const hide = (placement: LettrPromoPlacement) => {
        if (placement === 'lettr_template_card') {
            shouldDisplayLettrPromoTemplateCard.value = false;
        }
        if (placement === 'lettr_banner') {
            shouldDisplayLettrPromoBanner.value = false;
        }
        if (placement === 'lettr_sidebar') {
            shouldDisplayLettrPromoSidebar.value = false;
        }
    };

    const trackImpression = (placement: LettrPromoLabel) => {
        pushEvent('lettr_promo_impression', { placement });
    };

    /**
     * Hides the placement straight away so the promo does not sit there while
     * the request is in flight; a failed request only means it comes back on
     * the next page load, which is preferable to blocking the interaction.
     */
    const dismiss = async (placement: LettrPromoPlacement) => {
        hide(placement);
        pushEvent('lettr_promo_dismiss', { placement });

        try {
            await axios.put(route('users.promo-dismissals.update'), {
                placement,
            });
        } catch (err) {
            return;
        }
    };

    /**
     * Lands on the integrations page with the Lettr modal already open - the
     * promo asked for an API key, so making the user find Lettr in a grid of
     * thirty integrations first is a step for nothing.
     */
    const connect = (placement: LettrPromoLabel) => {
        pushEvent('lettr_promo_click', { placement });

        router.visit(
            route('integrations.available.index', {
                [CONNECT_QUERY_PARAM]: 'lettr',
            })
        );
    };

    /**
     * Kept apart from `lettr_promo_click` so the funnel can tell someone
     * heading for the connect flow from someone going off to read about Lettr.
     * For a real anchor, which should carry the href itself.
     */
    const trackLearnMore = (placement: LettrPromoLabel) => {
        pushEvent('lettr_promo_learn_more', { placement });
    };

    /**
     * For readers who cannot connect anything from where they are standing:
     * Lettr's own site has to do the convincing.
     */
    const learnMore = (placement: LettrPromoLabel) => {
        trackLearnMore(placement);

        window.open(lettrHomeUrl(placement), '_blank', 'noopener');
    };

    return {
        learnMore,
        trackLearnMore,
        shouldDisplayLettrPromoTemplateCard,
        shouldDisplayLettrPromoBanner,
        shouldDisplayLettrPromoSidebar,
        trackImpression,
        dismiss,
        connect,
    };
};

export default useLettrPromo;
